/**
 * hooks/useChat.js
 * Вся навигационная бизнес-логика виджета.
 * Компоненты вызывают методы этого хука — не меняют экран напрямую.
 *
 * sendFreeText — универсальный обработчик основного чатового инпута.
 * Ведёт себя по-разному в зависимости от текущего экрана:
 *   NAME     → сохранить имя, перейти на COMPANY
 *   COMPANY  → сохранить компанию, перейти на CONTACTS
 *   FAQ      → сохранить вопрос, обработать ответ
 *   иначе    → перейти на FAQ
 *
 * Ключевые слова навигации (menu / back / меню / назад и др.)
 * всегда возвращают пользователя на WelcomeScreen независимо от экрана.
 */
import { useCallback } from 'react';
import { useWidget } from '@/state/WidgetContext';
import { SCREENS } from '@/utils/constants';
import { useLead } from './useLead';
import { liveChatService } from '@/services/liveChat';
import { validateContactsForm, isFormValid } from '@/utils/validators';

/** Слова, при вводе которых открывается главное меню */
const MENU_KEYWORDS = /^(menu|меню|back|назад|main|home|start|старт|вернуться|return|начало|главная)$/i;

export function useChat() {
  const { goTo, setInterest, setChannel, setSelectedEvent, state } = useWidget();
  const { submitLead, setName, setCompany, setQuestion } = useLead();

  /** Выбор сценария на экране 2 */
  const chooseScenario = useCallback((value) => {
    setInterest('scenario', value);
    switch (value) {
      case 'events':  goTo(SCREENS.INDUSTRY); break;
      case 'forum':   goTo(SCREENS.CATCHUP);  break;
      case 'consult': goTo(SCREENS.NAME);     break;
      case 'faq':     goTo(SCREENS.FAQ);      break;
      default:        goTo(SCREENS.INDUSTRY);
    }
  }, [goTo, setInterest]);

  /** Выбор отрасли → следующий шаг */
  const chooseIndustry = useCallback((value) => {
    setInterest('industry', value);
    goTo(SCREENS.GOAL);
  }, [goTo, setInterest]);

  /** Выбор цели */
  const chooseGoal = useCallback((value) => {
    setInterest('goal', value);
    goTo(SCREENS.GEOGRAPHY);
  }, [goTo, setInterest]);

  /** Выбор географии */
  const chooseGeography = useCallback((value) => {
    setInterest('geography', value);
    goTo(SCREENS.FORMAT);
  }, [goTo, setInterest]);

  /** Выбор формата → результаты */
  const chooseFormat = useCallback((value) => {
    setInterest('format', value);
    goTo(SCREENS.RESULTS);
  }, [goTo, setInterest]);

  /** Клик по карточке мероприятия */
  const chooseEvent = useCallback((eventId) => {
    setSelectedEvent(eventId);
    goTo(SCREENS.EVENT_DETAIL);
  }, [goTo, setSelectedEvent]);

  /** Выбор канала связи */
  const chooseChannel = useCallback(async (value) => {
    setChannel(value);

    if (value === 'email') {
      goTo(SCREENS.EMAIL_CONFIRM);
      return;
    }

    await submitLead({ channel: value });
    goTo(SCREENS.MANAGER);
  }, [goTo, setChannel, submitLead]);

  /** Email подтверждён — переходим к описанию обращения */
  const confirmEmailCorrect = useCallback(() => {
    goTo(SCREENS.EMAIL_INQUIRY);
  }, [goTo]);

  /** Email неверный — экран редактирования */
  const editEmail = useCallback(() => {
    goTo(SCREENS.EMAIL_EDIT);
  }, [goTo]);

  /** После исправления email — к описанию обращения */
  const submitEditedEmail = useCallback(() => {
    goTo(SCREENS.EMAIL_INQUIRY);
  }, [goTo]);

  /** Финальная отправка email-заявки с доп. полями */
  const submitEmailInquiry = useCallback(async () => {
    const ok = await submitLead({
      channel: 'email',
      contact_by_email: true,
    });
    goTo(ok ? SCREENS.OFFLINE : SCREENS.LEAD_ERROR);
    return ok;
  }, [submitLead, goTo]);

  /** Кнопка Contact an expert: если контакты заполнены, сразу в выбор канала */
  const contactExpert = useCallback(() => {
    const errors = validateContactsForm(state.user.leadData);
    if (isFormValid(errors)) {
      goTo(SCREENS.CHANNEL);
      return;
    }
    goTo(SCREENS.CONTACTS);
  }, [goTo, state.user.leadData]);

  /** Переход к выбору канала (лид отправится после выбора канала) */
  const submitContacts = useCallback(() => {
    goTo(SCREENS.CHANNEL);
  }, [goTo]);

  /** Отправка email для гайда — создаёт лид с guide_requested: true */
  const submitGuideEmail = useCallback(async () => {
    const ok = await submitLead({ guide_requested: true });
    goTo(ok ? SCREENS.OFFLINE : SCREENS.LEAD_ERROR);
    return ok;
  }, [submitLead, goTo]);

  /**
   * Универсальный обработчик основного чатового инпута (Footer).
   * Сначала проверяет навигационные ключевые слова,
   * затем ведёт себя по-разному в зависимости от текущего экрана.
   */
  const sendFreeText = useCallback((text) => {
    // Навигационные слова — всегда возвращают в главное меню
    if (MENU_KEYWORDS.test(text.trim())) {
      goTo(SCREENS.WELCOME);
      return;
    }

    switch (state.screen) {
      case SCREENS.NAME:
        setName(text);
        goTo(SCREENS.COMPANY);
        break;

      case SCREENS.COMPANY:
        setCompany(text);
        goTo(SCREENS.CONTACTS);
        break;

      case SCREENS.FAQ: {
        setQuestion(text);
        // это симуляция, заменить на реальный вызов
        const understood = Math.random() > 0.5;
        goTo(understood ? SCREENS.OFFLINE : SCREENS.FAQ_UNSURE);
        break;
      }

      case SCREENS.LIVE_CHAT:
      case SCREENS.MANAGER:
        liveChatService.send(text).catch((error) => {
          console.error('[LiveChat] Failed to send message:', error);
        });
        break;

      default:
        goTo(SCREENS.FAQ);
    }
  }, [state.screen, goTo, setName, setCompany, setQuestion]);

  return {
    chooseScenario,
    chooseIndustry,
    chooseGoal,
    chooseGeography,
    chooseFormat,
    chooseEvent,
    contactExpert,
    chooseChannel,
    confirmEmailCorrect,
    editEmail,
    submitEditedEmail,
    submitEmailInquiry,
    submitContacts,
    submitGuideEmail,
    sendFreeText,
  };
}